import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  TextField,
  Button,
  Tooltip,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import { InventoryRequestsService } from '../../../api';
import { StatusChip as StatusChipCommon, ViewButton } from '../../common/Table/Table.styles';
import { TableStatusChip } from '../../common/Table/TableStatusChip';
import { mapRequestStatus, mapRequestItemStatus } from '../../common/status';
import EmptyStateMessage from '../../common/EmptyStateMessage';
import { ConfirmationModal } from '../../ui/Modal';
import InventoryDetailModal from '../InventoryDetailModal';
import RequestHistorySection from './RequestHistorySection';
import { useTabScopedOpen } from '../../dashboard/TabPanelActiveContext';
import {
  buildInitialResolutionState,
  buildResolvePayload,
  type ItemResolutionState,
  type RequestDetail,
} from './InventoryRequests.hooks';
import type { NotificationSeverity } from '../../../utils/constants/notificationMessages';
import type { InventoryItem } from '../../../types/inventory';
import { mapWarehouseInventoryProductApiToInventoryItem } from '../../../utils/inventory/mapWarehouseInventoryProductToInventoryItem';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CloseButton,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  ModernButton,
  insetPanelPaperSx,
} from '../InventoryTransfer/InventoryTransfer.styles';

const transferPrimaryButtonSx = {
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(to right, #0891b2, #059669)',
  },
};

export interface RequestDetailsModalProps {
  open: boolean;
  /** 'resolve' only takes effect when `canResolve` is also true. */
  mode: 'view' | 'resolve';
  isLoadingDetails: boolean;
  requestDetail: RequestDetail | null;
  canResolve: boolean;
  onClose: () => void;
  /** Called after a successful resolve so the parent can refresh the list. */
  onResolved: () => void;
  onNotify: (message: string, severity: NotificationSeverity) => void;
  onOpenLinkedTransfer: (transferId: number, transferCode?: string) => void;
}

const RequestDetailsModal: React.FC<RequestDetailsModalProps> = ({
  open,
  mode,
  isLoadingDetails,
  requestDetail,
  canResolve,
  onClose,
  onResolved,
  onNotify,
  onOpenLinkedTransfer,
}) => {
  const scopedOpen = useTabScopedOpen(open);
  const isResolveMode = mode === 'resolve' && canResolve;

  const [itemResolution, setItemResolution] = useState<ItemResolutionState>({});
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [isResolving, setIsResolving] = useState(false);

  const pieceDetailContextRef = useRef<{ requestId: number; wipId: number } | null>(null);
  const [pieceDetailOpen, setPieceDetailOpen] = useState(false);
  const [pieceDetailItem, setPieceDetailItem] = useState<InventoryItem | null>(null);
  const [pieceDetailFetching, setPieceDetailFetching] = useState(false);
  const [pieceDetailFetchError, setPieceDetailFetchError] = useState<string | null>(null);

  const closePieceDetailModal = useCallback(() => {
    pieceDetailContextRef.current = null;
    setPieceDetailOpen(false);
    setPieceDetailItem(null);
    setPieceDetailFetchError(null);
    setPieceDetailFetching(false);
  }, []);

  useEffect(() => {
    if (isResolveMode && requestDetail?.items?.length) {
      setItemResolution(buildInitialResolutionState(requestDetail.items));
    } else {
      setItemResolution({});
    }
  }, [requestDetail, isResolveMode]);

  useEffect(() => {
    if (!open) {
      closePieceDetailModal();
    }
  }, [open, closePieceDetailModal]);

  const reloadPieceDetailFromRequest = useCallback(async () => {
    const ctx = pieceDetailContextRef.current;
    if (!ctx) return;
    try {
      const res =
        await InventoryRequestsService.inventoryRequestsControllerFindWarehouseInventoryProductForRequest(
          ctx.requestId,
          ctx.wipId,
        );
      const data = res?.data ?? res;
      setPieceDetailItem(mapWarehouseInventoryProductApiToInventoryItem(data));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const openPieceDetail = useCallback(
    async (warehouseInventoryProductId: number) => {
      const requestId = requestDetail?.id ? Number(requestDetail.id) : null;
      if (!requestId || !warehouseInventoryProductId) return;
      pieceDetailContextRef.current = { requestId, wipId: warehouseInventoryProductId };
      setPieceDetailFetchError(null);
      setPieceDetailItem(null);
      setPieceDetailOpen(false);
      setPieceDetailFetching(true);
      try {
        const res =
          await InventoryRequestsService.inventoryRequestsControllerFindWarehouseInventoryProductForRequest(
            requestId,
            warehouseInventoryProductId,
          );
        const data = res?.data ?? res;
        const ctx = pieceDetailContextRef.current;
        if (!ctx || ctx.requestId !== requestId || ctx.wipId !== warehouseInventoryProductId) {
          return;
        }
        setPieceDetailItem(mapWarehouseInventoryProductApiToInventoryItem(data));
        setPieceDetailOpen(true);
      } catch (err: unknown) {
        const ctx = pieceDetailContextRef.current;
        if (!ctx || ctx.requestId !== requestId || ctx.wipId !== warehouseInventoryProductId) {
          return;
        }
        console.error(err);
        const msg =
          (err as { body?: { message?: string } })?.body?.message ||
          (err as Error)?.message ||
          'No se pudo cargar el detalle de la pieza.';
        setPieceDetailFetchError(msg);
      } finally {
        const ctx = pieceDetailContextRef.current;
        if (ctx && ctx.requestId === requestId && ctx.wipId === warehouseInventoryProductId) {
          setPieceDetailFetching(false);
        }
      }
    },
    [requestDetail?.id],
  );

  const handleToggleItemApproval = useCallback((itemId: number, approved: boolean) => {
    setItemResolution((prev) => ({
      ...prev,
      [itemId]: {
        approved,
        rejectionReason: approved ? '' : (prev[itemId]?.rejectionReason ?? ''),
      },
    }));
  }, []);

  const handleRejectionReasonChange = useCallback((itemId: number, reason: string) => {
    setItemResolution((prev) => ({
      ...prev,
      [itemId]: {
        approved: prev[itemId]?.approved ?? false,
        rejectionReason: reason,
      },
    }));
  }, []);

  const approvedCount = useMemo(
    () => Object.values(itemResolution).filter((item) => item.approved).length,
    [itemResolution],
  );

  const rejectedCount = useMemo(
    () => Object.values(itemResolution).filter((item) => !item.approved).length,
    [itemResolution],
  );

  const handleClose = useCallback(() => {
    setShowResolveConfirm(false);
    onClose();
  }, [onClose]);

  const executeResolve = useCallback(async () => {
    if (requestDetail == null || !canResolve || requestDetail.status !== 'PENDING') {
      return;
    }

    setIsResolving(true);
    setShowResolveConfirm(false);

    try {
      await InventoryRequestsService.inventoryRequestsControllerResolve(
        Number(requestDetail.id),
        buildResolvePayload(itemResolution),
      );

      onNotify('Solicitud resuelta con éxito.', 'success');
      handleClose();
      onResolved();
    } catch (error: unknown) {
      console.error('Error al resolver solicitud:', error);
      const message =
        (error as { body?: { message?: string } })?.body?.message ??
        (error as { message?: string })?.message ??
        'No se pudo resolver la solicitud.';
      onNotify(message, 'error');
    } finally {
      setIsResolving(false);
    }
  }, [canResolve, handleClose, itemResolution, onNotify, onResolved, requestDetail]);

  const handleOpenLinkedTransfer = useCallback(() => {
    if (!requestDetail?.transferId) return;
    handleClose();
    onOpenLinkedTransfer(requestDetail.transferId, requestDetail.transferCode ?? undefined);
  }, [handleClose, onOpenLinkedTransfer, requestDetail?.transferCode, requestDetail?.transferId]);

  return (
    <>
      <StyledDialog
        open={scopedOpen}
        onClose={handleClose}
        aria-labelledby="request-details-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle id="request-details-dialog-title">
          <Typography variant="h6" component="span">
            {isResolveMode ? 'Resolver solicitud' : 'Detalle de solicitud'}
          </Typography>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </StyledDialogTitle>

        <StyledDialogContent>
          {isLoadingDetails ? (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>Cargando detalle...</LoadingText>
            </LoadingContainer>
          ) : requestDetail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper sx={(theme) => insetPanelPaperSx(theme)}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Código SOL
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {requestDetail.requestCode}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Estado
                    </Typography>
                    {(() => {
                      const chip = mapRequestStatus(requestDetail.statusLabel);
                      return <TableStatusChip label={chip.label} color={chip.color} size="small" />;
                    })()}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Solicitud Origen
                    </Typography>
                    <Typography variant="body2">{requestDetail.destination}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Solicitud Destino
                    </Typography>
                    <Typography variant="body2">{requestDetail.source}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Solicitado por
                    </Typography>
                    <Typography variant="body2">{requestDetail.createdBy}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Fecha de solicitud
                    </Typography>
                    <Typography variant="body2">{requestDetail.date}</Typography>
                  </Grid>
                  {requestDetail.resolvedAt && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Fecha de resolución
                      </Typography>
                      <Typography variant="body2">{requestDetail.resolvedAt}</Typography>
                    </Grid>
                  )}
                  {requestDetail.resolvedBy && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Resuelto por
                      </Typography>
                      <Typography variant="body2">{requestDetail.resolvedBy}</Typography>
                    </Grid>
                  )}
                  {requestDetail.transferCode && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Transferencia generada
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        sx={{ p: 0, minWidth: 0, fontWeight: 600, textTransform: 'none' }}
                        onClick={handleOpenLinkedTransfer}
                      >
                        {requestDetail.transferCode}
                      </Button>
                    </Grid>
                  )}
                  {requestDetail.notes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Notas del solicitante
                      </Typography>
                      <Typography variant="body2">{requestDetail.notes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  Piezas ({requestDetail.items.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Tipo</TableCell>
                        {isResolveMode ? (
                          <>
                            <TableCell align="center">Aprobar</TableCell>
                            <TableCell>Motivo rechazo</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>Estado</TableCell>
                            <TableCell>Motivo rechazo</TableCell>
                          </>
                        )}
                        <TableCell align="center">Ver</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requestDetail.items.map((item) => {
                        const resolution = itemResolution[item.id];
                        const chip = mapRequestItemStatus(item.status);

                        return (
                          <TableRow key={item.id}>
                            <TableCell>{item.productCode}</TableCell>
                            <TableCell>{item.productName}</TableCell>
                            <TableCell>{item.productType}</TableCell>
                            {isResolveMode ? (
                              <>
                                <TableCell align="center">
                                  <Switch
                                    size="small"
                                    checked={resolution?.approved ?? false}
                                    onChange={(e) =>
                                      handleToggleItemApproval(item.id, e.target.checked)
                                    }
                                    color="success"
                                  />
                                </TableCell>
                                <TableCell>
                                  {!resolution?.approved && (
                                    <TextField
                                      size="small"
                                      fullWidth
                                      placeholder="Opcional"
                                      value={resolution?.rejectionReason ?? ''}
                                      onChange={(e) =>
                                        handleRejectionReasonChange(item.id, e.target.value)
                                      }
                                    />
                                  )}
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell>
                                  <StatusChipCommon
                                    label={chip.label}
                                    size="small"
                                    color={chip.color}
                                  />
                                </TableCell>
                                <TableCell>{item.rejectionReason ?? '—'}</TableCell>
                              </>
                            )}
                            <TableCell align="center" sx={{ py: 0.5 }}>
                              {item.warehouseInventoryProductId != null &&
                              item.warehouseInventoryProductId > 0 ? (
                                <Tooltip title="Ver detalle de la pieza">
                                  <ViewButton
                                    size="small"
                                    aria-label="Ver detalle de la pieza"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void openPieceDetail(item.warehouseInventoryProductId!);
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </ViewButton>
                                </Tooltip>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {isResolveMode && (
                <Typography variant="body2" color="text.secondary">
                  Resumen: {approvedCount} aprobada(s), {rejectedCount} rechazada(s).
                </Typography>
              )}

              {!isResolveMode && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Historial
                  </Typography>
                  <RequestHistorySection requestId={Number(requestDetail.id)} />
                </Box>
              )}
            </Box>
          ) : (
            <EmptyStateMessage message="No se encontró la solicitud" minHeight={120} />
          )}
        </StyledDialogContent>

        <StyledDialogActions>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            {isResolveMode ? (
              <>
                <ModernButton variant="tonal" onClick={handleClose}>
                  Cancelar
                </ModernButton>
                {requestDetail && (
                  <ModernButton
                    variant="contained"
                    onClick={() => setShowResolveConfirm(true)}
                    disabled={isResolving || requestDetail.items.length === 0}
                    startIcon={<CheckCircleIcon />}
                    sx={{ ...transferPrimaryButtonSx, marginLeft: 'auto' }}
                  >
                    Resolver solicitud
                  </ModernButton>
                )}
              </>
            ) : (
              <ModernButton variant="tonal" onClick={handleClose} sx={{ marginLeft: 'auto' }}>
                Cerrar
              </ModernButton>
            )}
          </Box>
        </StyledDialogActions>
      </StyledDialog>

      <ConfirmationModal
        open={showResolveConfirm}
        onClose={() => setShowResolveConfirm(false)}
        onConfirm={executeResolve}
        title="¿Confirmar resolución?"
        message={`Se aprobarán ${approvedCount} pieza(s) y se rechazarán ${rejectedCount}. ${
          approvedCount > 0
            ? 'Se generará una transferencia con las piezas aprobadas.'
            : 'No se generará transferencia.'
        }`}
        confirmText="Confirmar"
        cancelText="Cancelar"
        icon={<CheckCircleIcon />}
        confirmButtonGradient="linear-gradient(to right, #06b6d4, #10b981)"
        confirmButtonGradientHover="linear-gradient(to right, #0891b2, #059669)"
        loading={isResolving}
      />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 2 }}
        open={pieceDetailFetching}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog
        open={Boolean(pieceDetailFetchError)}
        onClose={() => setPieceDetailFetchError(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Alert severity="error">{pieceDetailFetchError}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPieceDetailFetchError(null)} color="inherit">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <InventoryDetailModal
        open={pieceDetailOpen && Boolean(pieceDetailItem)}
        onClose={closePieceDetailModal}
        item={pieceDetailItem}
        readOnly
        onSave={() => {
          void reloadPieceDetailFromRequest();
        }}
        onItemUpdate={(updated) => setPieceDetailItem(updated)}
      />
    </>
  );
};

export default RequestDetailsModal;
